import { Typography } from 'antd'
import { memo, type ReactElement, useContext, useLayoutEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { chatRowsOf } from '../../domain/chatRows'
import { actionableKpiPlanIdOf, kpiPlanDecisionsOf } from '../../domain/kpiPlan'
import { type ChatItem, isThinking, type SessionChat } from '../../domain/sessionChat'
import { AgentChatContext, useAgentChatActions } from '../../state/agentWidgetContext'
import { AgentChatItem, AgentThinkingIndicator, AgentToolGroup } from './AgentChatItem'
import { KpiPlanContext, type KpiPlanContextValue } from './KpiPlanCard'

// いちばん下からこの距離（px）以内なら、下に張り付いているとみなし、回答が伸びたら下まで送る。上に戻って読んでいる間は動かさない
const STICK_TO_BOTTOM_PX = 48

const EMPTY_ITEMS: readonly ChatItem[] = []

/**
 * 会話の表示。playground の ChatPane から、会話の並びと自動のスクロールを移した。
 * 前の会話を読むこと（ページング）は、会話の履歴を読む #10112 で足す。このウィジェットで始めた会話には、読む前の会話がないためである
 */
const AgentChatPaneView = (): ReactElement => {
  const { t } = useTranslation()
  // 会話を読むのはこの部品だけにし、回答が伸びたときに描き直すのを会話の表示に限る
  const session: SessionChat | undefined = useContext(AgentChatContext)
  const items = session?.items ?? EMPTY_ITEMS
  const rows = useMemo(() => chatRowsOf(items), [items])
  const runningTurnId = session?.runningTurnId ?? null
  const isThinkingNow = session !== undefined && isThinking(session)
  // プランのカードに渡す、会話から決まる判断と、判断を送れるプラン。判断の文面は、次の指示として送る。
  // 判断済みかは会話に入った指示から決めるので、送れなかったとき（描いた後に実行が始まっていた）はカードが判断を待つまま残る。送ったかは見ない
  const { sendPrompt } = useAgentChatActions()
  const planContext = useMemo(
    (): KpiPlanContextValue => ({
      decisions: kpiPlanDecisionsOf(items),
      actionablePlanId: actionableKpiPlanIdOf(items, runningTurnId),
      send: (text): void => {
        sendPrompt(text)
      },
    }),
    [items, runningTurnId, sendPrompt],
  )
  const scrollReference = useRef<HTMLDivElement>(null)
  const isStuckToBottomReference = useRef(true)

  // 描く前にスクロールの位置を決める。描いた後に送ると、いったん上から出てから下へ動くので、会話が長いとちらつく
  useLayoutEffect(() => {
    const element = scrollReference.current
    if (element !== null && isStuckToBottomReference.current) {
      element.scrollTop = element.scrollHeight
    }
  }, [items, isThinkingNow])

  if (items.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <Typography.Text type="secondary">{t(`エージェントに指示できます`)}</Typography.Text>
      </div>
    )
  }

  return (
    <div
      ref={scrollReference}
      className="min-h-0 flex-1 overflow-y-auto py-2"
      onScroll={(event) => {
        const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
        isStuckToBottomReference.current = scrollHeight - scrollTop - clientHeight <= STICK_TO_BOTTOM_PX
      }}
    >
      <KpiPlanContext.Provider value={planContext}>
        <div className="flex flex-col gap-3">
          {rows.map((row) =>
            row.kind === 'item' ? (
              <AgentChatItem key={row.item.id} item={row.item} />
            ) : (
              <AgentToolGroup key={row.id} group={row} />
            ),
          )}
          {isThinkingNow && (
            <AgentThinkingIndicator
              // 送ったターンごとに作り直して、待った時間を数え直す。仮の turnId から API の turnId に付け替わったときに
              // 作り直すと、数え直しで「考え中」に一瞬戻るので、turnId ではなく送った時刻で分ける
              key={session.runningTurnWaiting?.sentAt ?? runningTurnId}
              stage={session.runningTurnStage}
              waiting={session.runningTurnWaiting}
            />
          )}
        </div>
      </KpiPlanContext.Provider>
    </div>
  )
}

// 入力欄の文字が変わって枠が描き直されても、会話が変わっていなければ描き直さない
export const AgentChatPane = memo(AgentChatPaneView)
